import { registerPlugin } from '@capacitor/core'
import { isNativeApp } from '../lib/native'
import { AIM_HOST, AIM_KEEPALIVE, AIM_KEEPALIVE_INTERVAL_MS, AIM_TCP_PORT, AIM_UDP_PORT, AimError, type AimBridge } from './aim'

/**
 * JS side of the in-app `Solo2Socket` Capacitor plugin (web/ios/App/App/Solo2SocketPlugin.swift).
 * It exposes the two sockets a browser cannot open: a UDP keepalive and a raw TCP stream.
 * Payloads cross the bridge as base64.
 */
interface Solo2SocketPlugin {
  udpStart(o: { host: string; port: number; payload: string; intervalMs: number; timeoutMs: number }): Promise<{ reply: string; route?: string; warning?: string }>
  tcpConnect(o: { host: string; port: number; timeoutMs: number }): Promise<{ route?: string } | void>
  udpStop(): Promise<void>
  tcpWrite(o: { data: string }): Promise<void>
  tcpRead(o: { timeoutMs: number; capacity: number }): Promise<{ data: string }>
  tcpClose(): Promise<void>
  wifiStatus(): Promise<{ wifi: boolean; status: string; interfaces: string[]; ip?: string; expensive: boolean }>
}

const Solo2Socket = registerPlugin<Solo2SocketPlugin>('Solo2Socket')

/** True when the native socket plugin is present (iOS app), false on the website. */
export const solo2Available = isNativeApp

export function toBase64(bytes: Uint8Array): string {
  let s = ''
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return btoa(s)
}

export function fromBase64(s: string): Uint8Array {
  const bin = atob(s)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

function wrap<T>(p: Promise<T>, code: string): Promise<T> {
  return p.catch((e) => {
    const msg = e instanceof Error ? e.message : String(e)
    const native = (e as { code?: string })?.code
    throw new AimError(msg, native || code)
  })
}

function abortable<T>(p: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return p
  if (signal.aborted) return Promise.reject(new AimError('Operation cancelled', 'CANCELLED'))
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(new AimError('Operation cancelled', 'CANCELLED'))
    signal.addEventListener('abort', onAbort, { once: true })
    p.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort))
  })
}

export interface WifiStatus {
  wifi: boolean
  status: string
  interfaces: string[]
  /** The phone's IPv4 address on Wi-Fi; on the Solo 2 hotspot it is 10.0.0.x */
  ip?: string
  expensive: boolean
}

/** Whether the phone currently has a Wi-Fi path at all (the Solo 2 hotspot counts). */
export function wifiStatus(): Promise<WifiStatus> {
  return wrap(Solo2Socket.wifiStatus(), 'WIFI_STATUS_FAILED')
}

/** AimBridge backed by the native plugin. */
export class NativeSolo2Bridge implements AimBridge {
  /** Human-readable notes from the native side (which interface/port was used), for the log. */
  notes: string[] = []

  async discover(signal?: AbortSignal): Promise<Uint8Array> {
    const r = await abortable(
      wrap(
        Solo2Socket.udpStart({
          host: AIM_HOST,
          port: AIM_UDP_PORT,
          payload: toBase64(new TextEncoder().encode(AIM_KEEPALIVE)),
          intervalMs: AIM_KEEPALIVE_INTERVAL_MS,
          timeoutMs: 8_000,
        }),
        'AIM_NO_REPLY',
      ),
      signal,
    )
    const reply = r.reply ? fromBase64(r.reply) : new Uint8Array(0)
    this.notes.push(`udp ${reply.length ? 'reply ' + reply.length + 'B' : 'no reply'}${r.route ? ' ' + r.route : ''}${r.warning ? ' ' + r.warning : ''}`)
    return reply
  }
  stopDiscovery(): Promise<void> {
    return wrap(Solo2Socket.udpStop(), 'UDP_STOP_FAILED')
  }
  async tcpConnect(signal?: AbortSignal): Promise<void> {
    const r = await abortable(wrap(Solo2Socket.tcpConnect({ host: AIM_HOST, port: AIM_TCP_PORT, timeoutMs: 10_000 }), 'TCP_CONNECT_FAILED'), signal)
    this.notes.push(`tcp connected${r && 'route' in r && r.route ? ' ' + r.route : ''}`)
  }
  tcpClose(): Promise<void> {
    return wrap(Solo2Socket.tcpClose(), 'TCP_CLOSE_FAILED')
  }
  tcpWrite(data: Uint8Array, signal?: AbortSignal): Promise<void> {
    return abortable(wrap(Solo2Socket.tcpWrite({ data: toBase64(data) }), 'TCP_WRITE_FAILED'), signal)
  }
  async tcpRead(timeoutMs: number, capacity = 65_535, signal?: AbortSignal): Promise<Uint8Array> {
    const r = await abortable(wrap(Solo2Socket.tcpRead({ timeoutMs, capacity }), 'TCP_READ_FAILED'), signal)
    return r.data ? fromBase64(r.data) : new Uint8Array(0)
  }
}
